import { useEffect, useRef, useState } from "react";

type Versioned = { version?: number; updatedAt?: string; updatedBy?: string };

/**
 * An object open in a form changed on the server, through another operator (SW-RS-14: no silent loss).
 *
 * The form keeps its own state across revisions. When a new version of the object arrives:
 * - nothing typed yet: the form takes the new version ([adopt]);
 * - a change in progress: the input is kept, untouched, and the new version is returned in `changed` so the
 *   form can warn. Saving then sends the old version and ends in the usual 409 conflict, which shows both.
 *
 * [comparable] gives the editable content of an object (normalised, without the fields the map drives),
 * used to tell whether the operator typed something since the version the form started from.
 */
export function useForeignUpdate<T extends Versioned>(incoming: T, edited: T, isNew: boolean | undefined,
  adopt: (v: T) => void, comparable: (v: T) => unknown): { changed: T | null; dismiss(): void } {
  const base = useRef(incoming);
  const [changed, setChanged] = useState<T | null>(null);
  useEffect(() => {
    if (isNew || incoming.version == null || incoming.version === base.current.version) return;
    const key = (v: T) => JSON.stringify(comparable({ ...v, version: undefined, updatedAt: undefined, updatedBy: undefined }));
    const dirty = key(edited) !== key(base.current);
    base.current = incoming;
    if (dirty) setChanged(incoming);
    else { adopt(incoming); setChanged(null); }
  }, [incoming.version]); // eslint-disable-line react-hooks/exhaustive-deps
  return { changed, dismiss: () => setChanged(null) };
}
